import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import { CollapsiblePanel } from '@ge/components/collapsible-panel';
import { Icon, Icons } from '@ge/components/icon';
import { Loader } from '@ge/components/loader';
import { ContentType, DateTimeFormats, Placeholders, QueryKey } from '@ge/models/constants';
import { Config, useIsMobile } from '@ge/shared/data-hooks';
import {
  CRTrackerColumnDefs,
  CRTrackerColumns,
} from '@ge/shared/models/table-col-defs/cr-tracker-table-cols';
import { fetchCRTrackerLockPeriodApi } from '@ge/shared/services/cr_tracker_lock_period';
import { killEventPropagation } from '@ge/shared/util';
import { globalColors } from '@ge/tokens/colors';

import { useCRTrackerContext } from '../../context/cr-tracker-context';
import { defaultCrTrackerCols } from '../../models/cr-tracker-table-cols-default';
import { downloadFile } from '../../util';

import CRContractTable from './cr-contacts-table';

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  -webkit-justify-content: space-between;
`;

const PanelHeaderTitle = styled.div`
  display: flex;
  align-items: center;
`;

const PanelHeaderTitleSite = styled.span`
  display: inline-block;
  font-size: 18px;
  margin-right: 20px;
`;

const CACollapsiblePanel = styled(CollapsiblePanel)`
  margin-bottom: 2px;
  .title {
    padding: 0 15px 0 7px;
    border-bottom: none;
    background-color: ${(props) => props.theme.analyze.manualAdjustment.panelsColor};
    cursor: pointer;
    svg {
      fill: ${globalColors.slate3};
    }
  }
  ${(props) =>
    props.isMobile
      ? `.collapsible-panel-content {
      &.panel-loading,
      &.no-overflow {
        overflow: auto !important;
      }
    }`
      : ''}
`;

const SitesIcons = styled(Icon).attrs((props) => ({
  size: props.size || 22,
  color: props.theme.analyze.manualAdjustment.iconsColor,
}))`
  margin-right: 4px;
`;

const Count = styled.span`
  margin-right: 14px;
  display: inline-block;
  font-size: 16px;
`;

const DownloadIcon = styled(Icon).attrs(() => ({
  size: 12,
  color: globalColors.slate4,
}))``;

const StyledButton = styled.button`
  color: ${globalColors.slate4};
  background: none;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  padding: 0;
  svg {
    margin: 0;
    display: flex;
  }
`;

export const CRContractsPanel = ({ isOverviewLoading, contract }) => {
  const { ready } = useTranslation(['analyze.cr-tracker'], {
    useSuspense: false,
  });

  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const { selectedContract, handleSiteExpand } = useCRTrackerContext();

  const panelExpanded = useMemo(() => {
    return selectedContract && selectedContract.contractId === contract?.contractId;
  }, [contract?.contractId, selectedContract]);

  const handleFileDownload = useCallback(
    async (e, contract) => {
      killEventPropagation(e);
      try {
        const contractData = await queryClient.fetchQuery({
          queryKey: [QueryKey.CR_TRACKER_LOCK_PERIOD_TABLE, contract.contractId],
          queryFn: () =>
            fetchCRTrackerLockPeriodApi({
              contractId: contract.contractId,
              currentDate: dayjs().format('YYYY-MM-DD'),
            }),
          ...Config.EXECUTE_ONCE,
        });

        const filename = `CR_Tracker_And_Lock_Period-${
          contract?.contractName || ''
        }-${dayjs().format(DateTimeFormats.FILE_DOWNLOAD_DATE_TIME)}`;

        const fields = Object.values(CRTrackerColumnDefs).flatMap((group) =>
          Object.values(group.cols).flatMap((col) =>
            (col.labels || []).map((label) => label.a11yDefault).filter(Boolean),
          ),
        );
        fields.unshift('Contract Name');
        const columns = defaultCrTrackerCols.flatMap((group) =>
          (group.cols || []).map((col) => col.id),
        );
        columns.unshift('Contract_Name');

        const data = (contractData?.data || []).map((row) =>
          columns.map((col) => {
            switch (col) {
              case CRTrackerColumns.MONTH:
                return row?.month_date || Placeholders.DASH;
              case CRTrackerColumns.CONTRACTUAL_AVAILABILITY:
                return row?.contractual_availability || Placeholders.DASH;
              case 'Contract_Name':
                return contract?.contractName || Placeholders.DASH;
              case CRTrackerColumns.MALS_ADDED:
                return row?.mals_added || Placeholders.DASH;
              case CRTrackerColumns.MALS_APPROVED:
                return row?.mals_approved || Placeholders.DASH;
              case CRTrackerColumns.MALS_NOT_VALIDATED:
                return row?.mals_not_validated || Placeholders.DASH;
              case CRTrackerColumns.MALS_REJECTED:
                return row?.mals_rejected || Placeholders.DASH;
              case CRTrackerColumns.STATUS_UPDATE:
                return row?.status_after_lock || Placeholders.DASH;
              case CRTrackerColumns.MALS_SUBMISSION_DATE:
                return row?.mals_after_lock || Placeholders.DASH;
              case CRTrackerColumns.LOCK_DONE_BY_SSO:
                return row?.locked_by || Placeholders.DASH;
              case CRTrackerColumns.LOCK_MONTH:
                return row?.lock_flag;
              case CRTrackerColumns.LOCK_MONTH_DATE:
                return row?.lock_date
                  ? dayjs
                      .unix(row?.lock_date)
                      .tz(row?.timezone)
                      .format(DateTimeFormats.YEAR_MONTH_DAY)
                  : Placeholders.DASH;
              default:
                return row?.[col] || Placeholders.DASH;
            }
          }),
        );
        downloadFile({
          fields,
          data,
          filename,
          type: ContentType.CSV,
        });
      } catch (error) {
        console.error('error', error);
      }
    },
    [queryClient],
  );

  if (!ready || isOverviewLoading) {
    return <Loader />;
  }

  return (
    <CACollapsiblePanel
      headerContent={
        <PanelHeader>
          <PanelHeaderTitle>
            <PanelHeaderTitleSite>{contract?.contractName}</PanelHeaderTitleSite>
            <SitesIcons icon={Icons.TURBINE} size={16} />
            <Count>{contract?.srcassetid?.length}</Count>
          </PanelHeaderTitle>
          <StyledButton onClick={(e) => handleFileDownload(e, contract)} type="button">
            <DownloadIcon viewbox={'0 0 12 12'} icon={Icons.EXPORT} />
          </StyledButton>
        </PanelHeader>
      }
      onToggleExpand={() => handleSiteExpand(contract)}
      expanded={panelExpanded}
      isMobile={isMobile}
    >
      {panelExpanded ? <CRContractTable contractId={contract?.contractId} /> : <div></div>}
    </CACollapsiblePanel>
  );
};

CRContractsPanel.propTypes = {
  isOverviewLoading: PropTypes.bool,
  contract: PropTypes.object.isRequired,
};
